import { useCallback, useRef } from 'react';

import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import {
  type ExtraAppPreviewResponse,
  type StudioAppUsageResponse,
  type StudioPlanId,
  useGetExtraAppsPreviewQuery,
  useGetStudioAppUsageQuery,
  usePurchaseExtraAppMutation,
} from 'src/services/api/studioCreditApi';

import { isUsageAtLimit } from './appLimit';

export interface AppLimitState {
  /**
   * Whether this surface applies at all: the `studio-pricing` flag is on and this
   * is a Studio workspace. Call sites gate on it so the flag reads as an explicit
   * condition rather than only an implicit one inside {@link isAtAppLimit}.
   */
  isStudioPricingWorkspace: boolean;
  /** Whether commissioning another app would exceed the plan's allowance. */
  isAtAppLimit: boolean;
  /**
   * Whether the allowance is still being read. {@link isAtAppLimit} fails open
   * while it is, so call sites must hold the action back until this clears or a
   * fast click simply races the paywall and skips it.
   */
  isResolving: boolean;
  /** Resolved Studio plan, or null while unresolved / on Classic. */
  studioPlan: StudioPlanId | null;
  /** Apps the plan includes, per the server; 0 until the read resolves. */
  includedApps: number;
  /** Apps the server counts against the allowance; 0 until the read resolves. */
  countableApps: number;
  /**
   * Stripe's quote for one more app, once the warm read lands. Undefined off
   * the fee route or while in flight — surfaces fall back to unpriced copy.
   */
  extraAppFee: ExtraAppPreviewResponse | undefined;
  /**
   * Charges the per-app fee. Resolves true once the charge settles and false on
   * a refusal, so the caller holds the gated action instead of starting work a
   * declined card was meant to pay for.
   */
  purchaseExtraApp: () => Promise<boolean>;
  /** Whether the per-app charge is in flight. */
  isPurchasingExtraApp: boolean;
  /**
   * Re-reads the count and resolves with it. An add elsewhere in the session
   * moves the count, so a call site about to gate awaits this rather than
   * deciding on a possibly stale cached value.
   *
   * The publish gate has no cached counterpart to {@link isAtAppLimit} for this
   * reason: this page's first read predates the app's own sidebar module, so a
   * publish decided on the cached count is a whole app behind.
   */
  refetchUsage: () => Promise<StudioAppUsageResponse | undefined>;
}

/**
 * Reads the workspace's app count against its plan allowance.
 *
 * The count comes from `/studio-app-usage`, which derives it from the same
 * portal settings the extra-apps billing meters — so the paywall, the server's
 * hard block on Free and the Stripe quantity can never disagree about how many
 * apps a workspace has. Every unresolved state — inert workspace, in-flight plan
 * or usage query — fails OPEN, since the backend enforces the allowance
 * independently and wrongly blocking is worse than wrongly allowing.
 */
export const useAppLimit = (): AppLimitState => {
  const { studio } = usePlanStatus();

  const { data, isLoading, refetch } = useGetStudioAppUsageQuery(undefined, {
    skip: !studio.isStudioPricingWorkspace,
  });

  // Null until the plan is genuinely known, so a workspace mid-load is never
  // mistaken for the freemium baseline the entitlement layer falls back to.
  const studioPlan =
    studio.isStudioPricingWorkspace && !studio.isCurrentPlanLoading
      ? studio.studioPlan
      : null;

  const isAtAppLimit = isUsageAtLimit(data);

  // Warms Stripe's per-app quote here, on the surfaces that can raise the
  // paywall, rather than inside the paywall itself: the modal mounts on the very
  // click it interrupts, so a quote requested there is a Stripe proration preview
  // the user waits out behind a skeleton. Subscribing (not one-shot prefetching)
  // also keeps the entry warm for as long as that surface is open. Skipped off
  // the fee route — Freemium is offered an upgrade instead, and the endpoint
  // rejects it outright.
  const canOfferPerAppFee =
    isAtAppLimit && studioPlan !== null && studioPlan !== 'freemium';
  const { data: extraAppFee } = useGetExtraAppsPreviewQuery(undefined, {
    skip: !canOfferPerAppFee,
  });

  const [purchaseExtraAppMutation, { isLoading: isPurchasingExtraApp }] =
    usePurchaseExtraAppMutation();
  const purchaseExtraApp = useCallback(async (): Promise<boolean> => {
    try {
      await purchaseExtraAppMutation().unwrap();
      return true;
    } catch {
      return false;
    }
  }, [purchaseExtraAppMutation]);

  // A failed re-read falls back to the cached response rather than resolving
  // undefined, so a transient network error can't read as "no limit" on a
  // workspace already known to be at one. Stable — the fallback reads through a
  // ref — so surfaces can refetch from an effect (e.g. the publish popover
  // re-reading the count on open) without each read re-arming the effect.
  const dataRef = useRef(data);
  dataRef.current = data;
  const refetchUsage = useCallback(async (): Promise<
    StudioAppUsageResponse | undefined
  > => {
    if (!studio.isStudioPricingWorkspace) {
      return undefined;
    }
    try {
      return await refetch().unwrap();
    } catch {
      return dataRef.current;
    }
  }, [studio.isStudioPricingWorkspace, refetch]);

  return {
    isStudioPricingWorkspace: studio.isStudioPricingWorkspace,
    // Both flags settle on failure as well as success, so a query that errors
    // returns to the advisory fails-open behaviour rather than stranding the
    // action behind a gate that never opens.
    isResolving:
      studio.isStudioPricingWorkspace &&
      (studio.isCurrentPlanLoading || isLoading),
    isAtAppLimit,
    studioPlan,
    includedApps: data?.includedApps ?? 0,
    countableApps: data?.countableApps ?? 0,
    extraAppFee,
    purchaseExtraApp,
    isPurchasingExtraApp,
    refetchUsage,
  };
};
